import React, { useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';
import block from 'bem-cn';
import PaginatorSwitcher from 'components/Paginator/PaginatorSwitcher/desktop';
import { balanceSortTypes, usernameSortTypes } from '../../../data';
import TableRow from '../TableRow/TableRow';

import './Table.scss';
import Select from 'components/Select/desktop/Select';

const b = block('users-table');

const Table = ({
  locale,
  currentPage,
  onPageClick,
  itemsOnPage,
  filterValue,
  balanceSortType,
  usernameSortType,
  onSetBalanceSort,
  onSetUsernameSort,
  role,
  dropdownCallback,
}) => {
  const { tab } = useParams();
  const totalCount = useSelector(state => state.users.usersList.totalCount, shallowEqual);
  const balanceSum = useSelector(state => state.users.usersList.balanceSum, shallowEqual);
  const usersList = useSelector(state => state.users.usersList, shallowEqual);
  const auth = useSelector(state => state.auth);
  const { selectedAgentId } = useSelector(state => state.tree);

  const users = useMemo(() => {
    const list = usersList.users;
    if (list.length && list[0].id === auth.id) return list.slice(1);
    return list;
  }, [usersList.users, auth.id]);

  const balanceTypes = balanceSortTypes.filter(type => !type?.isMobile);

  const rows = useMemo(
    () =>
      users
        .filter(t => (role !== '' ? Number(t.role) === Number(role) : true))
        .filter(el => {
          if (tab === 'disabled') {
            if (el.isBanned) return true;
            else return false;
          } else {
            return true;
          }
        })
        .map((item, i) => (
          <TableRow
            isWithdrawalAccess={auth.isWithdrawalAccess}
            isDepositAccess={auth.isDepositAccess}
            key={`${item.id}_${i}`}
            item={item}
            locale={locale}
            dropdownCallback={dropdownCallback}
          />
        )),
    [users, tab, auth, role, locale],
  );

  return (
    <div className={b({ tab })}>
      <div className={b('table')}>
        <div className={b('table-header')}>
          <div className={b('table-header-head')}>
            <span className={b('title')}>{locale.user}</span>
            <div className={b('select')}>
              <Select
                items={usernameSortTypes}
                activeItem={usernameSortType}
                onChange={onSetUsernameSort}
                color="transparent"
              />
            </div>
          </div>
          <div className={b('table-header-head', { balance: true })}>
            <span className={b('title')}>{locale.balance}</span>
            <div className={b('select')}>
              <Select
                items={balanceTypes}
                activeItem={balanceSortType}
                onChange={onSetBalanceSort}
                color="transparent"
              />
            </div>
          </div>

          <div className={b('table-header-head')}>{locale.operations}</div>
          <div className={b('table-header-head')}>{locale.date}</div>
          <div className={b('table-header-head')}>{locale.commissionBalance}</div>
          <div className={b('table-header-head')}>{locale.actions}</div>
        </div>
        <div className={b('tbody')}>
          {!!rows.length ? rows : <div className={b('no-results')}>{locale.noResults}</div>}
        </div>
      </div>
      <div className={b('paginator-wrapper')}>
        <div className={b('extra')}>
          <div className={b('extra-info')}>
            {totalCount} {locale.records}
          </div>
          {role !== '' && (
            <div className={b('extra-info')}>
              {locale.totalSaldo}: {balanceSum}
            </div>
          )}
        </div>
        <PaginatorSwitcher
          count={totalCount}
          currentPage={currentPage}
          onPageClick={onPageClick}
          itemsOnPage={itemsOnPage}
        />
      </div>
    </div>
  );
};

Table.propTypes = {
  usersList: PropTypes.array,
  count: PropTypes.number,
  currentPage: PropTypes.number.isRequired,
  itemsOnPage: PropTypes.object.isRequired,
  onPageClick: PropTypes.func.isRequired,
  filterValue: PropTypes.string.isRequired,
  balanceSortType: PropTypes.object.isRequired,
  onSetBalanceSort: PropTypes.func.isRequired,
};

export default Table;
